// useCollapsingHero — ONE implementation of the collapsing hero for the four
// pillar tabs (2026-10-23). Passive scroll listener + requestAnimationFrame;
// writes transform and opacity straight onto the hero element (no React
// re-render per frame, no height/layout animation); React state changes only
// when the collapsed boolean flips, so the top bar can swap. With
// prefers-reduced-motion the hero is left alone and only the state swaps.
//
// Build 41 fix: the hero element is captured through a CALLBACK ref held in
// state, and the effect is keyed on the tab, so switching between pillar tabs
// (a new hero element under the same ref object, `enabled` unchanged)
// re-attaches to the new element every time and resets the collapsed state.
import { useCallback, useEffect, useRef, useState } from 'react';
import { heroCollapseState } from '../lib/collapsingHeader';

export function useCollapsingHero({ scrollRef, enabled, tabKey }) {
  const [heroEl, setHeroEl] = useState(null);
  const heroRef = useCallback((el) => setHeroEl(el || null), []);
  const [collapsed, setCollapsed] = useState(false);
  const collapsedRef = useRef(false);
  useEffect(() => {
    const scroller = scrollRef.current;
    const hero = heroEl;
    collapsedRef.current = false; setCollapsed(false); // a tab switch starts expanded
    if (!enabled || !scroller || !hero) return undefined;
    let reduced = false;
    try { reduced = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches); } catch {}
    let raf = 0;
    let height = hero.offsetHeight || 1;
    const apply = () => {
      raf = 0;
      const st = heroCollapseState(scroller.scrollTop, height);
      if (!reduced) { hero.style.transform = st.transform; hero.style.opacity = String(st.opacity); }
      if (st.collapsed !== collapsedRef.current) { collapsedRef.current = st.collapsed; setCollapsed(st.collapsed); }
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(apply); };
    const onResize = () => { height = hero.offsetHeight || 1; onScroll(); };
    scroller.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    apply();
    return () => {
      scroller.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      if (raf) cancelAnimationFrame(raf);
      hero.style.transform = ''; hero.style.opacity = '';
      collapsedRef.current = false; setCollapsed(false);
    };
  }, [scrollRef, heroEl, enabled, tabKey]);
  return { collapsed, heroRef };
}
